import type { Shot } from '../lib/types'

/* ===========================================================================
 *  De scènes
 *
 *  Zes tekeningen die bewegen. Samen zijn ze het beeldmateriaal van alle
 *  filmpjes in de cursus: een film is een rij scènes met een tekst eronder,
 *  en een scène verwijst hiernaar met een naam.
 *
 *  Waarom getekend en niet gefilmd
 *  -------------------------------
 *
 *  Zie de kop van Film in lib/types.ts. Kort: dit is te corrigeren, te lezen
 *  zonder geluid, het weegt niets op een tablet aan de rand van het wifi, en
 *  het haalt niets van buiten binnen.
 *
 *  Waarom de grap in de TIMING zit en niet in de tekening
 *  ------------------------------------------------------
 *
 *  Een getekend poppetje met een grappig gezicht is één keer leuk. Wat wél
 *  blijft werken is een beat: het gaat goed, het gaat goed, en dan gebeurt er
 *  iets wat je niet had zien aankomen -- het BSN-pasje dat achter de rest
 *  aansluipt, de norm die van het papier valt. Vandaar dat elke scène één
 *  gebeurtenis heeft en niet drie.
 *
 *  Hoe ze afspelen
 *  ---------------
 *
 *  Alles in CSS, in styles/film.css. De speler geeft het element een sleutel
 *  per scène; React hangt hem dan opnieuw op en de animaties beginnen vanaf
 *  het begin. Geen tijdlijn in JavaScript die met de speler in de pas moet
 *  blijven -- twee klokken lopen altijd een keer uit elkaar.
 *
 *  Alles staat in één viewBox van 640 bij 360, zodat de speler hem gewoon kan
 *  uitrekken tot de breedte die er is.
 * =========================================================================== */

export default function Shots({ shot }: { shot: Shot }) {
  return (
    <svg
      className="shot"
      viewBox="0 0 640 360"
      role="presentation"
      aria-hidden
      preserveAspectRatio="xMidYMid meet"
    >
      {teken(shot)}
    </svg>
  )
}

function teken(shot: Shot) {
  switch (shot) {
    case 'naar-buiten':  return <NaarBuiten />
    case 'voorspellen':  return <Voorspellen />
    case 'verzinsel':    return <Verzinsel />
    case 'poort':        return <Poort />
    case 'handtekening': return <Handtekening />
    case 'masker':       return <Masker />
    case 'binnenkomst':  return <Binnenkomst />
    case 'lezen':        return <Lezen />
    case 'vierogen':     return <VierOgen />
    case 'naar-exact':   return <NaarExact />
  }
}

/* ------------------------------------------------------------------ *
 *  1. Wat je typt, verlaat het bedrijf
 *
 *  De beat: een keurige vakvraag vliegt naar de server -- en er sluipt een
 *  tweede berichtje achteraan dat er niet hoort te zijn.
 * ------------------------------------------------------------------ */

function NaarBuiten() {
  return (
    <g>
      {/* de hal */}
      <rect x="28" y="150" width="190" height="140" rx="10" className="v-beton" />
      <path d="M28 150 L123 96 L218 150 Z" className="v-geel" />
      <rect x="86" y="206" width="74" height="48" rx="5" className="v-donker" />
      <rect x="92" y="212" width="62" height="30" rx="3" className="v-water-zacht" />
      <rect x="78" y="254" width="90" height="7" rx="3" className="v-lijn" />
      <text x="123" y="316" className="v-label" textAnchor="middle">ons bedrijf</text>

      {/* de grens */}
      <line x1="330" y1="70" x2="330" y2="300" className="v-grens" />
      <text x="330" y="58" className="v-label" textAnchor="middle">de grens</text>

      {/* de server */}
      <rect x="470" y="140" width="128" height="150" rx="10" className="v-beton" />
      {[0, 1, 2, 3].map((i) => (
        <g key={i}>
          <rect x="484" y={158 + i * 33} width="100" height="22" rx="4" className="v-donker" />
          <circle cx={572} cy={169 + i * 33} r="4" className="v-lamp" style={{ animationDelay: `${1.6 + i * 0.12}s` }} />
        </g>
      ))}
      <text x="534" y="316" className="v-label" textAnchor="middle">ergens anders</text>

      {/* de nette vraag */}
      <g className="v-vlucht-1">
        <rect x="0" y="-19" width="188" height="38" rx="19" className="v-bubbel" />
        <text x="16" y="5" className="v-bubbeltekst">Hoe krijg ik teer eraf?</text>
      </g>

      {/* en wat er stiekem achteraan komt */}
      <g className="v-vlucht-2">
        <rect x="0" y="-18" width="176" height="36" rx="18" className="v-bubbel-fout" />
        <text x="15" y="5" className="v-bubbeltekst">…en Jan z'n BSN is</text>
        <g className="v-kruis">
          <circle cx="176" cy="0" r="17" className="v-rond-fout" />
          <path d="M169 -7 L183 7 M183 -7 L169 7" className="v-streep-fout" />
        </g>
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  2. Hij weet niets, hij voorspelt
 *
 *  De beat: drie kandidaten, de zelfverzekerdste wint -- en dan blijkt er
 *  geen bron onder te liggen.
 * ------------------------------------------------------------------ */

function Voorspellen() {
  const kandidaten = [
    { woord: '250 draaiuren', breedte: 168, klas: 'v-balk-a' },
    { woord: 'week', breedte: 96, klas: 'v-balk-b' },
    { woord: 'dinsdag', breedte: 54, klas: 'v-balk-c' },
  ]

  return (
    <g>
      <text x="40" y="76" className="v-zin">De borstel moet elke</text>
      <rect x="318" y="52" width="176" height="34" rx="6" className="v-gat" />

      {kandidaten.map((k, i) => (
        <g key={k.woord} transform={`translate(40 ${146 + i * 56})`}>
          <rect width={k.breedte} height="34" rx="6" className={`v-balk ${k.klas}`} />
          <text x="12" y="23" className="v-kandidaat">{k.woord}</text>
        </g>
      ))}

      {/* de winnaar schuift het gat in */}
      <g className="v-winnaar">
        <rect width="176" height="34" rx="6" className="v-geel" />
        <text x="88" y="23" className="v-winnaartekst" textAnchor="middle">250 draaiuren</text>
      </g>

      {/* en dan het loepje */}
      <g className="v-loep">
        <circle cx="0" cy="0" r="34" className="v-loep-glas" />
        <circle cx="0" cy="0" r="34" className="v-loep-rand" />
        <line x1="24" y1="24" x2="48" y2="48" className="v-loep-steel" />
        <text x="0" y="6" className="v-loep-tekst" textAnchor="middle">bron?</text>
      </g>
      <text x="360" y="300" className="v-fluister v-fluister-in">…die stond nergens.</text>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  3. Het verzinsel
 *
 *  De beat: er komt een officieel ogende stempel op -- en dan valt de norm
 *  van het papier af.
 * ------------------------------------------------------------------ */

function Verzinsel() {
  const letters = 'NEN 4187-3'.split('')

  return (
    <g>
      <rect x="196" y="52" width="248" height="256" rx="10" className="v-papier" />
      {[0, 1, 2, 3, 4].map((i) => (
        <rect key={i} x="224" y={88 + i * 22} width={i === 4 ? 96 : 192} height="8" rx="4" className="v-regel" />
      ))}

      {/* de stempel */}
      <g className="v-stempel">
        <circle cx="320" cy="238" r="58" className="v-zegel" />
        <circle cx="320" cy="238" r="48" className="v-zegel-binnen" />
        <text x="320" y="232" className="v-zegel-tekst" textAnchor="middle">OFFICIEEL</text>
        <text x="320" y="252" className="v-zegel-klein" textAnchor="middle">bron: geen</text>
      </g>

      {/* en dan valt de norm eraf */}
      <g transform="translate(320 156)">
        {letters.map((l, i) => (
          <text
            key={i}
            x={(i - letters.length / 2) * 21 + 10}
            y="0"
            className="v-valletter"
            style={{ animationDelay: `${2.5 + i * 0.07}s` }}
            textAnchor="middle"
          >
            {l}
          </text>
        ))}
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  4. De poort
 *
 *  De beat: de band loopt, alles gaat netjes zijn bak in -- en dan probeert
 *  een pasje er ongezien langs te komen.
 * ------------------------------------------------------------------ */

function Poort() {
  return (
    <g>
      {/* de band */}
      <rect x="0" y="182" width="640" height="14" rx="7" className="v-band" />
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <rect key={i} x={i * 84} y="182" width="40" height="14" className="v-bandstreep"
              style={{ animationDelay: `${i * -0.25}s` }} />
      ))}

      {/* de bakken */}
      <g transform="translate(430 34)">
        <rect width="170" height="58" rx="10" className="v-bak-goed" />
        <text x="124" y="36" className="v-baklabel v-baklabel-goed" textAnchor="middle">MAG</text>
      </g>
      <g transform="translate(430 268)">
        <rect width="170" height="58" rx="10" className="v-bak-fout" />
        <text x="124" y="36" className="v-baklabel v-baklabel-fout" textAnchor="middle">NIET</text>
      </g>

      {/* een vakvraag: gaat omhoog */}
      <g className="v-stuk v-stuk-1">
        <rect x="-38" y="-20" width="76" height="40" rx="8" className="v-kaartje" />
        <path d="M-14 -2 h28 M-14 6 h18" className="v-kaartlijn" />
        <circle cx="0" cy="-9" r="5" className="v-water-vol" />
      </g>

      {/* een naambadge: gaat omlaag */}
      <g className="v-stuk v-stuk-2">
        <rect x="-38" y="-22" width="76" height="44" rx="8" className="v-kaartje" />
        <circle cx="-18" cy="-4" r="8" className="v-hoofd" />
        <path d="M0 -10 h26 M0 0 h20 M-30 12 h58" className="v-kaartlijn" />
      </g>

      {/* en het pasje dat het probeert */}
      <g className="v-stuk v-stuk-3">
        <rect x="-46" y="-24" width="92" height="48" rx="8" className="v-kaartje-fout" />
        <text x="0" y="-2" className="v-pasje" textAnchor="middle">BSN</text>
        <path d="M-28 10 h56" className="v-kaartlijn" />
        <g className="v-bons">
          <circle cx="0" cy="0" r="22" className="v-rond-fout" />
          <path d="M-9 -9 L9 9 M9 -9 L-9 9" className="v-streep-fout" />
        </g>
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  5. De handtekening
 *
 *  De beat: de machine ratelt een brief uit, en dan zet een mens er rustig
 *  een streep door het bedrag dat niemand heeft opgegeven.
 * ------------------------------------------------------------------ */

function Handtekening() {
  return (
    <g>
      {/* de machine */}
      <rect x="34" y="112" width="132" height="126" rx="12" className="v-beton" />
      <circle cx="78" cy="152" r="9" className="v-water-vol" />
      <circle cx="122" cy="152" r="9" className="v-water-vol" />
      <rect x="66" y="184" width="68" height="9" rx="4" className="v-lijn" />
      <rect x="150" y="164" width="76" height="12" rx="6" className="v-arm" />

      {/* de brief */}
      <rect x="238" y="70" width="216" height="220" rx="10" className="v-papier" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x="264" y={104 + i * 22} width={i === 2 ? 108 : 164} height="8" rx="4"
              className="v-regel v-regel-tik" style={{ animationDelay: `${0.5 + i * 0.22}s` }} />
      ))}

      {/* het bedrag dat niemand heeft genoemd */}
      <g className="v-bedrag">
        <text x="264" y="196" className="v-bedragtekst">€ 340,—</text>
        <line x1="258" y1="190" x2="372" y2="190" className="v-doorstreep" />
      </g>

      {/* en de handtekening eronder */}
      <path
        d="M266 250 c12 -22 24 10 36 -6 c10 -13 22 16 34 2 c8 -9 18 6 28 -4"
        className="v-krabbel"
      />
      <text x="266" y="278" className="v-label">jij</text>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  6. Het masker
 *
 *  De beat: het gezicht kantelt weg en eronder zit gewoon een apparaat. Geen
 *  onthulling met schrikeffect -- het punt is juist dat het onschuldig is
 *  zolang je het zegt.
 * ------------------------------------------------------------------ */

function Masker() {
  return (
    <g>
      {/* het apparaat */}
      <rect x="236" y="106" width="168" height="152" rx="22" className="v-beton" />
      <rect x="284" y="84" width="72" height="22" rx="11" className="v-lijn" />
      <circle cx="320" cy="76" r="9" className="v-geel-vol" />
      <circle cx="282" cy="168" r="13" className="v-water-vol" />
      <circle cx="358" cy="168" r="13" className="v-water-vol" />
      <path d="M284 214 q36 26 72 0" className="v-mond" />

      {/* het masker dat wegkantelt */}
      <g className="v-masker">
        <rect x="236" y="106" width="168" height="152" rx="22" className="v-huid" />
        <circle cx="282" cy="168" r="10" className="v-donker" />
        <circle cx="358" cy="168" r="10" className="v-donker" />
        <path d="M288 212 q32 20 64 0" className="v-mond-huid" />
      </g>

      <g className="v-zeghet">
        <rect x="150" y="286" width="340" height="44" rx="22" className="v-bubbel" />
        <text x="320" y="314" className="v-bubbeltekst" textAnchor="middle">
          Je praat met een assistent.
        </text>
      </g>
    </g>
  )
}

/* ==========================================================================
 *  De vier scènes van de cursus over inkoopfacturen
 *
 *  Zelfde regel als hierboven: één gebeurtenis per scène, en de grap zit in
 *  de timing. Het verschil is dat deze vier een PROCEDURE uitbeelden en geen
 *  idee -- dus is elke scène ook letterlijk waar. Wie straks het scherm voor
 *  zich krijgt, moet het plaatje herkennen.
 * ========================================================================== */

/* ------------------------------------------------------------------ *
 *  7. De factuur komt binnen
 *
 *  De beat: de factuur valt netjes in het postvak -- en er komt een folder
 *  achteraan die niets met een factuur te maken heeft.
 * ------------------------------------------------------------------ */

function Binnenkomst() {
  return (
    <g>
      {/* de leverancier */}
      <rect x="26" y="150" width="150" height="130" rx="10" className="v-beton" />
      <path d="M26 150 L101 104 L176 150 Z" className="v-lijn" />
      <text x="101" y="308" className="v-label" textAnchor="middle">leverancier</text>

      {/* het postvak */}
      <rect x="446" y="150" width="168" height="130" rx="10" className="v-beton" />
      <rect x="462" y="178" width="136" height="22" rx="4" className="v-donker" />
      <rect x="462" y="208" width="136" height="22" rx="4" className="v-donker" />
      <rect x="462" y="238" width="136" height="22" rx="4" className="v-donker" />
      <text x="530" y="140" className="v-label" textAnchor="middle">postvak</text>
      <text x="530" y="308" className="v-adres" textAnchor="middle">inkoop.venlo@…</text>

      {/* de factuur vliegt erin */}
      <g className="v-post-1">
        <rect x="-44" y="-30" width="88" height="60" rx="5" className="v-papier" />
        <path d="M-44 -30 L0 6 L44 -30" className="v-envelop" />
        <text x="0" y="46" className="v-mini" textAnchor="middle">factuur</text>
      </g>

      {/* en de folder die er niets te zoeken heeft */}
      <g className="v-post-2">
        <rect x="-42" y="-28" width="84" height="56" rx="5" className="v-papier-grijs" />
        <text x="0" y="6" className="v-mini-donker" textAnchor="middle">reclame</text>
        <g className="v-stuit">
          <circle cx="0" cy="-42" r="19" className="v-rond-fout" />
          <path d="M-8 -50 L8 -34 M8 -50 L-8 -34" className="v-streep-fout" />
        </g>
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  8. De computer leest hem
 *
 *  De beat: de balk gaat over het vel, de velden rollen eruit -- en bij het
 *  laatste staat een vraagteken in plaats van een waarde.
 * ------------------------------------------------------------------ */

function Lezen() {
  const velden = [
    { naam: 'leverancier', waarde: 'Shell' },
    { naam: 'bedrag', waarde: '€ 1.247,50' },
    { naam: 'btw', waarde: '€ 216,42' },
  ]

  return (
    <g>
      {/* het vel */}
      <rect x="40" y="48" width="226" height="264" rx="8" className="v-papier" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect key={i} x="64" y={82 + i * 26} width={i % 3 === 2 ? 108 : 178} height="8" rx="4"
              className="v-regel" />
      ))}
      <rect x="64" y="252" width="130" height="16" rx="4" className="v-regel-dik" />

      {/* de leesbalk die eroverheen gaat */}
      <rect x="40" y="48" width="226" height="5" className="v-leesbalk" />

      {/* de velden die eruit komen */}
      {velden.map((v, i) => (
        <g key={v.naam} className={`v-veld v-veld-${i + 1}`}>
          <rect width="214" height="42" rx="8" className="v-vak" />
          <text x="14" y="26" className="v-veldnaam">{v.naam}</text>
          <text x="200" y="26" className="v-veldwaarde" textAnchor="end">{v.waarde}</text>
        </g>
      ))}

      {/* en het veld waar hij niet uitkwam */}
      <g className="v-veld v-veld-4">
        <rect width="214" height="42" rx="8" className="v-vak-twijfel" />
        <text x="14" y="26" className="v-veldnaam-twijfel">onderneming</text>
        <text x="200" y="26" className="v-veldwaarde-twijfel" textAnchor="end">?</text>
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  9. Vier ogen
 *
 *  De beat: de eerste tekent, de tweede tekent -- en als de eerste het
 *  nog eens probeert, gaat de stempel niet omlaag.
 * ------------------------------------------------------------------ */

function VierOgen() {
  return (
    <g>
      {/* de factuur in het midden */}
      <rect x="212" y="78" width="216" height="206" rx="10" className="v-papier" />
      {[0, 1, 2].map((i) => (
        <rect key={i} x="238" y={108 + i * 22} width={i === 2 ? 96 : 164} height="8" rx="4"
              className="v-regel" />
      ))}

      {/* twee mensen */}
      <g transform="translate(96 170)">
        <circle cx="0" cy="-34" r="24" className="v-hoofd-licht" />
        <path d="M-34 36 q34 -40 68 0 z" className="v-romp" />
        <text x="0" y="78" className="v-label" textAnchor="middle">Anja</text>
      </g>
      <g transform="translate(544 170)">
        <circle cx="0" cy="-34" r="24" className="v-hoofd-licht" />
        <path d="M-34 36 q34 -40 68 0 z" className="v-romp" />
        <text x="0" y="78" className="v-label" textAnchor="middle">Bram</text>
      </g>

      {/*
        * Twee lagen per stempel, en dat moet.
        *
        * De BUITENSTE g zet hem op zijn plek met een transform-attribuut; de
        * binnenste krijgt de animatie. Staan ze op hetzelfde element, dan
        * overschrijft de CSS-transform uit de keyframe het attribuut -- en
        * dan landt de stempel niet op de factuur maar linksboven in de hoek,
        * buiten beeld. Dat is precies wat hier eerst gebeurde.
        */}
      <g transform="translate(272 184)">
        <g className="v-stempel-1">
          <rect x="-52" y="-22" width="104" height="44" rx="8" className="v-stempel-goed" />
          <text x="0" y="6" className="v-stempeltekst-goed" textAnchor="middle">1e akkoord</text>
        </g>
      </g>

      <g transform="translate(368 238)">
        <g className="v-stempel-2">
          <rect x="-56" y="-22" width="112" height="44" rx="8" className="v-stempel-goed" />
          <text x="0" y="6" className="v-stempeltekst-goed" textAnchor="middle">2e tekening</text>
        </g>
      </g>

      {/* en de poging van dezelfde persoon */}
      <g transform="translate(320 316)">
        <g className="v-stempel-3">
          <rect x="-80" y="-20" width="160" height="40" rx="8" className="v-stempel-fout" />
          <text x="0" y="6" className="v-stempeltekst-fout" textAnchor="middle">
            zelfde persoon
          </text>
        </g>
      </g>
    </g>
  )
}

/* ------------------------------------------------------------------ *
 *  10. Naar Exact
 *
 *  De beat: de goedgekeurde gaat erin -- en de volgende stuit op een hek
 *  met de reden erop.
 * ------------------------------------------------------------------ */

function NaarExact() {
  return (
    <g>
      {/* de kluis */}
      <rect x="430" y="96" width="186" height="178" rx="12" className="v-beton" />
      <circle cx="523" cy="185" r="42" className="v-wiel" />
      <circle cx="523" cy="185" r="12" className="v-wiel-as" />
      <text x="523" y="302" className="v-label" textAnchor="middle">exact</text>

      {/* het hek */}
      <rect x="330" y="92" width="10" height="190" rx="5" className="v-hek" />

      {/* de goedgekeurde gaat erdoor */}
      <g className="v-bon-door">
        <rect x="-42" y="-28" width="84" height="56" rx="6" className="v-papier" />
        <path d="M-18 2 l12 12 l24 -26" className="v-vink" />
      </g>

      {/* en de volgende blijft staan */}
      <g className="v-bon-stuit">
        <rect x="-42" y="-28" width="84" height="56" rx="6" className="v-papier" />
        <rect x="-26" y="-10" width="52" height="7" rx="3" className="v-regel" />
        <rect x="-26" y="4" width="36" height="7" rx="3" className="v-regel" />
        <g className="v-reden">
          <rect x="-96" y="34" width="192" height="34" rx="17" className="v-bubbel-fout" />
          <text x="0" y="56" className="v-redentekst" textAnchor="middle">
            geen crediteur
          </text>
        </g>
      </g>
    </g>
  )
}
